import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Typography from "@material-ui/core/Typography";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";

import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SearchIcon from "@material-ui/icons/Search";
import FilterListIcon from "@material-ui/icons/FilterList";
import RotateLeftIcon from "@material-ui/icons/RotateLeft";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import Skeleton from "@material-ui/lab/Skeleton";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import AddIcon from "@material-ui/icons/Add";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import Pagination from "@material-ui/lab/Pagination";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import PeriodFilter from "../../components/PeriodFilter";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { Can } from "../../components/Can";
import { formatarTelefone, formatarNomeOuTelefone } from "../../utils/phone";

const reducer = (state, action) => {
  if (action.type === "SET_CONTACTS") {
    return action.payload;
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      // A atualizacao que chega pelo socket nao traz as etiquetas (elas vem dos
      // tickets, so na listagem): mantem as que ja estavam na tela.
      state[contactIndex] = {
        ...contact,
        tags: contact.tags || state[contactIndex].tags || []
      };
      return [...state];
    }
    // Contato que nao esta na pagina: so entra no topo se for NOVO (criado
    // agora). Antes, qualquer contato que mandava mensagem pulava para o topo.
    const criado = new Date(contact.createdAt || 0).getTime();
    const maisRecenteNaTela = Math.max(0, ...state.map((c) => new Date(c.createdAt || 0).getTime()));
    if (!criado || criado < maisRecenteNaTela) return state;
    return [contact, ...state];
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    return state.filter((c) => c.id !== contactId);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    padding: theme.spacing(3, 3, 4, 3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
    width: "100%",
    boxSizing: "border-box",
  },
  headerWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
  },
  headerTitleBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  countBadge: {
    fontSize: "0.75rem",
    fontWeight: 700,
    padding: "4px 10px",
    borderRadius: "20px",
    backgroundColor: theme.palette.type === "dark" ? "rgba(13,148,136, 0.2)" : "rgba(13,148,136, 0.1)",
    color: "#0D9488",
    border: "1px solid rgba(13,148,136, 0.25)",
  },
  headerButtons: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.2),
    flexWrap: "wrap",
  },
  btnPrimary: {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    padding: "8px 18px",
    backgroundColor: "#0D9488",
    color: "#ffffff",
    boxShadow: "0 2px 6px rgba(13,148,136, 0.25)",
    "&:hover": {
      backgroundColor: "#0f766e",
      boxShadow: "0 4px 12px rgba(13,148,136, 0.35)",
    },
  },
  btnOutlined: {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    padding: "8px 16px",
    borderColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.15)" : "#d0d5dd",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      borderColor: "#0D9488",
      backgroundColor: theme.palette.type === "dark" ? "rgba(13,148,136, 0.1)" : "rgba(13,148,136, 0.05)",
      color: "#0D9488",
    },
  },

  filterBar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderRadius: "14px",
    backgroundColor: theme.palette.background.paper,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 2px 10px rgba(0,0,0,.3)"
        : "0 2px 10px rgba(0,0,0,.04)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#f0f2f5"}`,
  },
  searchField: {
    flex: "1 1 240px",
    minWidth: 200,
    "& .MuiOutlinedInput-root": {
      height: 40,
      borderRadius: "8px",
      backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.04)" : "#f8fafc",
      fontSize: "0.875rem",
      "& fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0",
      },
      "&:hover fieldset": {
        borderColor: "#0D9488",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0D9488",
        borderWidth: 1.5,
      },
    },
  },
  formControl: {
    minWidth: 155,
    flex: "0 1 auto",
    cursor: "pointer",
    "& .MuiSelect-root": {
      cursor: "pointer",
      width: "100%",
      paddingTop: 10,
      paddingBottom: 10,
    },
    "& .MuiSelect-icon": { display: "none" },
    "& .MuiSelect-select": { paddingRight: "14px !important" },
    "& .MuiOutlinedInput-root": {
      height: 40,
      borderRadius: "8px",
      fontSize: "0.875rem",
      backgroundColor: theme.palette.background.paper,
      "& fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0",
      },
      "&:hover fieldset": {
        borderColor: "#0D9488",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0D9488",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.8rem",
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
  },
  filterActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginLeft: "auto",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      width: "100%",
      justifyContent: "flex-end",
    },
  },
  applyBtn: {
    height: 40,
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
    padding: "0 18px",
    backgroundColor: "#0D9488",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#0f766e",
    },
  },
  clearBtn: {
    height: 40,
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
    padding: "0 14px",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#f1f3f5",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#e4e7ec",
    },
  },

  // ---- Lista de contatos em linhas-cartao ----
  lista: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: 12,
  },
  linha: {
    display: "grid",
    gridTemplateColumns:
      "minmax(240px, 2.4fr) minmax(130px, 1fr) minmax(150px, 1.3fr) minmax(150px, 1.4fr) minmax(95px, .8fr) 116px",
    alignItems: "center",
    gap: 14,
    padding: "10px 14px",
    minWidth: 0,
    "& > *": { minWidth: 0 },
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "minmax(220px, 2fr) minmax(140px, 1.2fr) minmax(140px, 1.3fr) 116px",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr auto",
      "& > :nth-child(3), & > :nth-child(4)": { display: "none" },
    },
  },
  colDoc: { [theme.breakpoints.down("md")]: { display: "none !important" } },
  colConsulta: { [theme.breakpoints.down("md")]: { display: "none !important" } },
  cpfInline: {
    display: "none",
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
    [theme.breakpoints.down("md")]: { display: "block" },
  },
  cabecalho: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    padding: "8px 14px",
    fontSize: "0.68rem",
    fontWeight: 800,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    background: theme.palette.type === "dark" ? "rgba(15,23,42,0.92)" : "rgba(248,250,252,0.95)",
    backdropFilter: "blur(6px)",
    borderRadius: 10,
  },
  linhaCartao: {
    position: "relative",
    cursor: "pointer",
    borderRadius: 14,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(148,163,184,0.12)" : "#edf1f5"}`,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.02)" : "#ffffff",
    transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
    animation: "iaSubir .35s ease backwards",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 10,
      bottom: 10,
      width: 3,
      borderRadius: 3,
      background: "#14b8a6",
      opacity: 0,
      transition: "opacity .18s ease",
    },
    "&:hover": {
      transform: "translateY(-1px)",
      borderColor: theme.palette.type === "dark" ? "rgba(20,184,166,0.35)" : "rgba(20,184,166,0.35)",
      boxShadow: theme.palette.type === "dark" ? "0 10px 24px rgba(0,0,0,.4)" : "0 10px 22px rgba(15,23,42,.07)",
      "&::before": { opacity: 1 },
    },
  },
  linhaTelefone: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 3,
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
    "& > span:first-of-type": { whiteSpace: "nowrap" },
    "& [class*='chipConexao'], & [class*='chipSemConexao']": { marginTop: 0 },
  },
  blocoDoc: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    fontSize: "0.8rem",
    color: theme.palette.text.primary,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  rotuloMini: {
    display: "inline-block",
    width: 38,
    fontSize: "0.64rem",
    fontWeight: 800,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  celulaChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 5,
    alignItems: "center",
    "& > *": { margin: "0 !important", justifyContent: "flex-start !important" },
  },
  tablePaper: {
    borderRadius: "14px",
    backgroundColor: theme.palette.background.paper,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 4px 14px rgba(0,0,0,.35)"
        : "0 4px 14px rgba(0,0,0,.04)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#f0f2f5"}`,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  tableContainer: {
    overflowX: "auto",
    "&::-webkit-scrollbar": {
      height: 8,
      width: 8,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.15)" : "#cbd5e1",
      borderRadius: 4,
    },
  },
  table: {
    minWidth: 780,
  },
  tableHead: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tableCellHead: {
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    fontSize: "0.72rem",
    letterSpacing: "0.06em",
    padding: "14px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tableRow: {
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
    },
  },
  tableCell: {
    padding: "14px 16px",
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#f1f3f5"}`,
    fontSize: "0.875rem",
  },
  contactNameBox: {
    display: "flex",
    alignItems: "center",

    
    
    justifyContent: "flex-start",
    textAlign: "left",
    gap: theme.spacing(1.5),

    minWidth: 0,
  },
  avatar: {
    width: 38,
    height: 38,
    
    flexShrink: 0,
    backgroundColor: theme.palette.type === "dark" ? "#2a3448" : "#e8f5e9",
    color: "#0D9488",
    fontWeight: 700,
    fontSize: "0.9rem",
  },
  contactName: {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  contactEmail: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  contactNameCell: {
    textAlign: "left",
  },
  contactNameTexto: {
    minWidth: 0,
    overflow: "hidden",
  },
  chipConexao: {
    display: "inline-block",
    marginTop: 4,
    padding: "1px 8px",
    borderRadius: 6,
    fontSize: "0.66rem",
    fontWeight: 600,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#f1f5f9",
    color: theme.palette.text.secondary,
    maxWidth: 150,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  chipConexaoCaiu: {
    display: "inline-block",
    marginTop: 4,
    padding: "1px 8px",
    borderRadius: 6,
    fontSize: "0.66rem",
    fontWeight: 700,
    backgroundColor: "rgba(245,158,11,0.18)",
    color: "#b45309",
    maxWidth: 150,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  chipSemConexao: {
    display: "inline-block",
    marginTop: 4,
    padding: "1px 8px",
    borderRadius: 6,
    fontSize: "0.66rem",
    fontWeight: 800,
    backgroundColor: "#dc2626",
    color: "#fff",
    maxWidth: 160,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  phoneText: {
    fontSize: "0.82rem",
    color: theme.palette.text.primary,
    fontVariantNumeric: "tabular-nums",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,

    whiteSpace: "nowrap",
  },
  cpfText: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
  },
  dateText: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
  },
  bankChipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "center",
    justifyContent: "center",
    maxWidth: 260,
    margin: "0 auto",
  },
  eligibleChip: {
    height: 24,
    fontSize: "0.72rem",
    fontWeight: 700,
    backgroundColor: theme.palette.type === "dark" ? "rgba(13,148,136, 0.25)" : "#e8f5e9",
    color: "#0D9488",
    border: "1px solid rgba(13,148,136, 0.3)",
    borderRadius: "6px",
  },
  valueChip: {
    height: 24,
    fontSize: "0.72rem",
    fontWeight: 700,
    backgroundColor: theme.palette.type === "dark" ? "rgba(59, 130, 246, 0.2)" : "#eff6ff",
    color: "#2563eb",
    border: "1px solid rgba(59, 130, 246, 0.3)",
    borderRadius: "6px",
  },
  analysisChip: {
    height: 24,
    fontSize: "0.72rem",
    fontWeight: 700,
    backgroundColor: theme.palette.type === "dark" ? "rgba(245, 158, 11, 0.18)" : "#fffbeb",
    color: "#d97706",
    border: "1px solid rgba(245, 158, 11, 0.35)",
    borderRadius: "6px",
  },
  tagChip: {
    color: "#fff",
    padding: "2px 8px",
    borderRadius: 6,
    fontSize: "0.7rem",
    fontWeight: 600,
    lineHeight: "18px",
    whiteSpace: "nowrap",
    display: "inline-block",
  },
  ineligibleChip: {
    height: 24,
    fontSize: "0.72rem",
    fontWeight: 700,
    backgroundColor: theme.palette.type === "dark" ? "rgba(239, 68, 68, 0.18)" : "#fef2f2",
    color: "#ef4444",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: "6px",
  },
  emptyText: {
    color: theme.palette.text.disabled,
    fontSize: "0.85rem",
  },
  actionButtons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 4,
  },
  actionBtn: {
    padding: 6,
    borderRadius: "8px",
    color: theme.palette.text.secondary,
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "#f1f3f5",
      color: theme.palette.text.primary,
    },
  },
  actionBtnWhatsapp: {
    padding: 6,
    borderRadius: "8px",
    color: "#0D9488",
    "&:hover": {
      backgroundColor: "rgba(13,148,136, 0.12)",
      color: "#0f766e",
    },
  },
  actionBtnDelete: {
    padding: 6,
    borderRadius: "8px",
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: "rgba(239, 68, 68, 0.12)",
      color: "#ef4444",
    },
  },

  paginationFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.01)" : "#fcfcfd",
  },
  paginationInfo: {
    fontSize: "0.82rem",
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  paginationControls: {
    "& .MuiPaginationItem-root": {
      borderRadius: "8px",
      fontWeight: 600,
      fontSize: "0.82rem",
    },
    "& .Mui-selected": {
      backgroundColor: "#0D9488 !important",
      color: "#fff",
    },
  },

  
  
  emptyState: {
    padding: theme.spacing(6, 2),
    textAlign: "center",
  },
  emptyStateBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
  },
  emptyIcon: {
    fontSize: 48,
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(1),
  },
}));

const formatCPF = (cpf) => {
  if (!cpf || cpf === "-" || cpf === "") return "-";
  const clean = String(cpf).replace(/\D/g, "");
  if (clean.length === 11) {
    return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6, 9)}-${clean.slice(9)}`;
  }
  return cpf;
};

const Contacts = () => {
  const classes = useStyles();
  const history = useHistory();

  const { user } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [whatsappsList, setWhatsappsList] = useState([]);
  const [queuesList, setQueuesList] = useState([]);
  const [tagsList, setTagsList] = useState([]);

  const [period, setPeriod] = useState({
    dateFrom: "",
    dateTo: "",
    label: "Data de Criação",
  });
  const [connectionFilter, setConnectionFilter] = useState("all");
  const [queueFilter, setQueueFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [tipoFilter, setTipoFilter] = useState("all");

  const [appliedFilters, setAppliedFilters] = useState({
    dateFrom: "",
    dateTo: "",
    connectionFilter: "all",
    queueFilter: "all",
    tagFilter: "all",
    tipoFilter: "all",
  });

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [whatsRes, queuesRes, tagsRes] = await Promise.all([
          api.get("/whatsapp"),
          api.get("/queue"),
          api.get("/tags"),
        ]);
        setWhatsappsList(whatsRes.data || []);
        setQueuesList(queuesRes.data || []);
        setTagsList(tagsRes.data || []);
      } catch (err) {
        console.error("Erro ao carregar filas, conexões e tags:", err);
      }
    };
    fetchDropdowns();
  }, []);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        searchParam: searchParam.trim(),
        pageNumber,
      };

      if (appliedFilters.dateFrom) {
        params.dateFrom = appliedFilters.dateFrom;
      }
      if (appliedFilters.dateTo) {
        params.dateTo = appliedFilters.dateTo;
      }
      if (appliedFilters.connectionFilter && appliedFilters.connectionFilter !== "all") {
        params.whatsapps = String(appliedFilters.connectionFilter);
      }
      if (appliedFilters.queueFilter && appliedFilters.queueFilter !== "all") {
        params.queues = String(appliedFilters.queueFilter);
      }
      if (appliedFilters.tagFilter && appliedFilters.tagFilter !== "all") {
        params.tags = String(appliedFilters.tagFilter);
      }
      if (appliedFilters.tipoFilter && appliedFilters.tipoFilter !== "all") {
        params.tipo = appliedFilters.tipoFilter;
      }

      const { data } = await api.get("/contacts/", { params });
      dispatch({ type: "SET_CONTACTS", payload: data.contacts || [] });
      setTotalCount(data.count || 0);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  }, [searchParam, pageNumber, appliedFilters]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchContacts();
    }, 350);
    return () => clearTimeout(delayDebounceFn);
  }, [fetchContacts]);

  useEffect(() => {
    const socket = openSocket();

    const onContact = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
        setTotalCount((prev) => Math.max(0, prev - 1));
      }
    };
    socket.on("contact", onContact);

    return () => {
      socket.off("contact", onContact);
    };
  }, []);

  const handleSearchChange = (event) => {
    setSearchParam(event.target.value);
    setPageNumber(1);
  };

  const handleApplyFilters = () => {
    setPageNumber(1);
    setAppliedFilters({
      dateFrom: period.dateFrom,
      dateTo: period.dateTo,
      connectionFilter,
      queueFilter,
      tagFilter,
      tipoFilter,
    });
  };

  const handleClearFilters = () => {
    const defaultPeriod = { dateFrom: "", dateTo: "", label: "Data de Criação" };
    setPeriod(defaultPeriod);
    setConnectionFilter("all");
    setQueueFilter("all");
    setTagFilter("all");
    setTipoFilter("all");
    setSearchParam("");
    setPageNumber(1);
    setAppliedFilters({
      dateFrom: "",
      dateTo: "",
      connectionFilter: "all",
      queueFilter: "all",
      tagFilter: "all",
      tipoFilter: "all",
    });
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleSaveTicket = async (contactId) => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data: ticket } = await api.post("/tickets", {
        contactId,
        userId: user?.id,
        status: "open",
      });
      history.push(`/atendimentos/${ticket.id}`);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
      dispatch({ type: "DELETE_CONTACT", payload: contactId });
      setTotalCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
  };

  const handleImportContacts = async () => {
    try {
      await api.post("/contacts/import");
      fetchContacts();
      toast.success("Contatos importados com sucesso!");
    } catch (err) {
      toastError(err);
    }
  };

  const handleExportContacts = async () => {
    try {
      const { data } = await api.get("/contacts/", {
        params: {
          searchParam,
          exportContact: "true",
          dateFrom: appliedFilters.dateFrom,
          dateTo: appliedFilters.dateTo,
          whatsapps: appliedFilters.connectionFilter !== "all" ? appliedFilters.connectionFilter : undefined,
          queues: appliedFilters.queueFilter !== "all" ? appliedFilters.queueFilter : undefined,
          tags: appliedFilters.tagFilter !== "all" ? appliedFilters.tagFilter : undefined,
          tipo: appliedFilters.tipoFilter !== "all" ? appliedFilters.tipoFilter : undefined,
        },
      });
      const contactsToExport = data.contacts || [];

      const extraFieldNames = new Set();
      contactsToExport.forEach((contact) => {
        if (contact.extraInfo) {
          contact.extraInfo.forEach((info) => extraFieldNames.add(info.name));
        }
      });
      const extraFieldsArray = Array.from(extraFieldNames);

      const header = ["Nome", "Número", "Email", ...extraFieldsArray];
      const csvData = contactsToExport.map((contact) => {
        const row = [
          `"${contact.name || ""}"`,
          `="${contact.number || ""}"`,
          `"${contact.email || ""}"`,
        ];

        extraFieldsArray.forEach((field) => {
          const info = contact.extraInfo?.find((i) => i.name === field);
          row.push(`"${info ? info.value : ""}"`);
        });

        return row.join(";");
      });

      const csvContent = "\ufeff" + [header.join(";"), ...csvData].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "contatos.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      toastError(err);
    }
  };

  const getContactField = (contact, fieldSuffix) => {
    const info = contact?.extraInfo?.find(
      (i) =>
        i.name.toLowerCase() === fieldSuffix.toLowerCase() ||
        i.name.toLowerCase().endsWith(fieldSuffix.toLowerCase())
    );
    return info ? info.value : "-";
  };

  // O valor pode estar gravado como numero cru ("4003.073479794035", vindo da
  // IA) ou ja formatado ("R$ 4.003,07", vindo da tela). O chip mostrava o texto
  // como veio, e o numero cru aparecia inteiro na lista.
  const moedaDoRegistro = (valor) => {
    if (valor === null || valor === undefined) return "";

    const texto = String(valor).trim();
    if (!texto || texto === "-") return "";

    let limpo = texto.replace(/[^\d,.-]/g, "");
    if (!limpo) return "";

    if (limpo.includes(",")) {
      limpo = limpo.replace(/\./g, "").replace(",", ".");
    } else if ((limpo.match(/\./g) || []).length > 1) {
      // Sem virgula o texto veio como numero cru do motor, e ali o ponto e
      // SEMPRE decimal ("4003.073479794035"). So tratamos ponto como milhar
      // quando ha mais de um — "1.234.567". A regra antiga olhava "3 digitos
      // depois do ponto" e lia "1234.567" como 1.234.567.
      limpo = limpo.replace(/\./g, "");
    }

    const n = Number(limpo);
    if (!Number.isFinite(n) || n <= 0) return "";

    return `R$ ${n.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // Entrevista do candidato (a proxima marcada ou, se nao houver, a ultima).
  const renderEntrevista = (contact) => {
    const e = contact?.entrevista;
    if (!e) return <span className={classes.emptyText}>Não agendada</span>;
    const quando = new Date(e.startAt);
    const passou = quando.getTime() < Date.now();
    const texto = quando.toLocaleString("pt-BR", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
    return (
      <Chip
        size="small"
        label={texto}
        className={passou ? classes.analysisChip : classes.eligibleChip}
      />
    );
  };

  const renderVaga = (contact) => {
    const vaga = contact?.entrevista?.jobTitle;
    return vaga ? (
      <Typography variant="body2" noWrap title={vaga}>{vaga}</Typography>
    ) : (
      <span className={classes.emptyText}>-</span>
    );
  };

  const renderSituacao = (contact) => {
    const e = contact?.entrevista;
    if (!e) return <span className={classes.emptyText}>-</span>;
    if (e.outcome === "compareceu") {
      return <Chip size="small" label="Compareceu" className={classes.eligibleChip} />;
    }
    if (e.outcome === "faltou") {
      return <Chip size="small" label="Faltou" className={classes.ineligibleChip} />;
    }
    return new Date(e.startAt).getTime() < Date.now() ? (
      <Chip size="small" label="Aguardando retorno" className={classes.analysisChip} />
    ) : (
      <Chip size="small" label="Agendada" className={classes.valueChip} />
    );
  };

  // Por qual numero nosso a empresa fala com este cliente. Quando a conexao
  // foi excluida, o vinculo some do banco -- por isso guardamos o nome dela
  // ("conexaoAnterior") e mostramos aqui, senao ninguem descobre mais de onde
  // aquela conversa saia.
  const chipInterno = (contact) =>
    contact?.interno ? (
      <span
        title="Contato interno: a I.A e o fluxo não respondem; as conversas ficam na aba Internos."
        style={{
          display: "inline-block", marginLeft: 6, padding: "1px 8px", borderRadius: 999,
          fontSize: "0.68rem", fontWeight: 800, color: "#6d28d9", background: "rgba(124,58,237,.12)",
        }}
      >
        Interno · {contact.tipoInterno || "Outro"}
      </span>
    ) : null;

  const renderConexao = (contact) => {
    const conexao = contact?.whatsapp;

    if (!contact?.whatsappId || !conexao) {
      if (!contact?.conexaoAnterior) return null;
      return (
        <span
          className={classes.chipSemConexao}
          title={`A conexão “${contact.conexaoAnterior}” não está mais no sistema. Abra o atendimento e use "Trocar conexão".`}
        >
          Sem conexão · era {contact.conexaoAnterior}
        </span>
      );
    }

    const caiu = conexao.status && conexao.status !== "CONNECTED";
    return (
      <span
        className={caiu ? classes.chipConexaoCaiu : classes.chipConexao}
        title={
          caiu
            ? `O número “${conexao.name}”, pelo qual falamos com este cliente, está desconectado.`
            : `Falamos com este cliente pela conexão “${conexao.name}”.`
        }
      >
        {conexao.name}
        {caiu ? " · desconectado" : ""}
      </span>
    );
  };

  const renderEtiquetas = (contact) => {
    const tags = contact?.tags || [];
    if (!tags.length) return <span className={classes.emptyText}>-</span>;
    return (
      <div className={classes.bankChipsContainer}>
        {tags.map((tag) => (
          <span
            key={tag.id}
            className={classes.tagChip}
            style={{ backgroundColor: tag.color || "#9e9e9e" }}
          >
            {tag.name}
          </span>
        ))}
      </div>
    );
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / 20));
  const startIndex = (pageNumber - 1) * 20 + 1;
  const endIndex = Math.min(pageNumber * 20, totalCount);

  return (
    <MainContainer>
      <div className={classes.pageWrapper}>
        <ContactModal
          open={contactModalOpen}
          onClose={handleCloseContactModal}
          aria-labelledby="form-dialog-title"
          contactId={selectedContactId}
        />
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${deletingContact.name}?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() =>
          deletingContact
            ? handleDeleteContact(deletingContact.id)
            : handleImportContacts()
        }
      >
        {deletingContact
          ? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>

      {}
      <Box className={classes.headerWrapper}>
        <Box className={classes.headerTitleBox}>
          <Title>{i18n.t("contacts.title")}</Title>
          <span className={classes.countBadge}>
            {totalCount} {totalCount === 1 ? "contato" : "contatos"}
          </span>
        </Box>

        <Box className={classes.headerButtons}>
          <Can
            role={user.profile}
            perform="contacts-page:export"
            yes={() => (
              <Button
                variant="outlined"
                startIcon={<CloudDownloadIcon />}
                className={classes.btnOutlined}
                onClick={handleExportContacts}
              >
                Exportar
              </Button>
            )}
          />
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            className={classes.btnOutlined}
            onClick={() => setConfirmOpen(true)}
          >
            {i18n.t("contacts.buttons.import")}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            className={classes.btnPrimary}
            onClick={handleOpenContactModal}
          >
            {i18n.t("contacts.buttons.add")}
          </Button>
        </Box>
      </Box>

      {}
      <Box className={classes.filterBar}>
        {}
        <TextField
          placeholder="Pesquisar por nome, número ou CPF..."
          type="search"
          variant="outlined"
          size="small"
          value={searchParam}
          onChange={handleSearchChange}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: "#9ca3af", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        {}
        <PeriodFilter value={period} onChange={setPeriod} />

        {}
        <FormControl variant="outlined" size="small" className={classes.formControl}>
          <InputLabel>Conexões</InputLabel>
          <Select
            MenuProps={{
              getContentAnchorEl: null,
              transitionDuration: 0,
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
            }}
            value={connectionFilter}
            onChange={(e) => setConnectionFilter(e.target.value)}
            label="Conexões"
          >
            <MenuItem value="all">Todas as conexões</MenuItem>
            {(whatsappsList.length > 0 ? whatsappsList : (whatsApps || [])).map((w) => (
              <MenuItem key={w.id} value={w.id}>
                {w.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {}
        {false && (
        <FormControl variant="outlined" size="small" className={classes.formControl}>
          <InputLabel>Filas</InputLabel>
          <Select
            MenuProps={{
              getContentAnchorEl: null,
              transitionDuration: 0,
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
            }}
            value={queueFilter}
            onChange={(e) => setQueueFilter(e.target.value)}
            label="Filas"
          >
            <MenuItem value="all">Todas as filas</MenuItem>
            {queuesList &&
              queuesList.map((q) => (
                <MenuItem key={q.id} value={q.id}>
                  {q.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        )}

        {}
        <FormControl variant="outlined" size="small" className={classes.formControl}>
          <InputLabel>Etiquetas</InputLabel>
          <Select
            MenuProps={{
              getContentAnchorEl: null,
              transitionDuration: 0,
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
            }}
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            label="Etiquetas"
          >
            <MenuItem value="all">Todas as etiquetas</MenuItem>
            {tagsList &&
              tagsList.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: t.color || "#999",
                      marginRight: 8,
                    }}
                  />
                  {t.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        {/* Tipo: candidatos x contatos internos (funcionário, banco, parceiro...) */}
        <FormControl variant="outlined" size="small" className={classes.formControl}>
          <InputLabel>Tipo</InputLabel>
          <Select
            MenuProps={{
              getContentAnchorEl: null,
              transitionDuration: 0,
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
            }}
            value={tipoFilter}
            onChange={(e) => setTipoFilter(e.target.value)}
            label="Tipo"
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="candidatos">Candidatos</MenuItem>
            <MenuItem value="internos">Contatos internos</MenuItem>
          </Select>
        </FormControl>

        {}
        <Box className={classes.filterActions}>
          <Button
            variant="contained"
            className={classes.applyBtn}
            startIcon={<FilterListIcon />}
            onClick={handleApplyFilters}
          >
            Aplicar filtros
          </Button>
          <Button
            variant="text"
            className={classes.clearBtn}
            startIcon={<RotateLeftIcon />}
            onClick={handleClearFilters}
          >
            Limpar
          </Button>
        </Box>
      </Box>

      {}
      <Paper className={classes.tablePaper} variant="outlined">
        {/* Lista em linhas-cartao: tudo cabe na largura da tela (sem rolagem
            lateral) e as acoes ficam sempre visiveis na direita da linha. */}
        <div className={classes.lista}>
          <div className={`${classes.linha} ${classes.cabecalho}`}>
            <span>Candidato</span>
            <span className={classes.colDoc}>Vaga</span>
            <span>Entrevista</span>
            <span>Etiquetas</span>
            <span className={classes.colConsulta}>Situação</span>
            <span style={{ textAlign: "right" }}>Ações</span>
          </div>

          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={`${classes.linha} ${classes.linhaCartao}`}>
                <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                  <Skeleton variant="circle" width={42} height={42} />
                  <Box flex={1}><Skeleton width="70%" /><Skeleton width="45%" /></Box>
                </Box>
                <Skeleton className={classes.colDoc} width="70%" />
                <Skeleton width="60%" />
                <Skeleton width="60%" />
                <Skeleton className={classes.colConsulta} width="50%" />
                <Skeleton width={80} style={{ marginLeft: "auto" }} />
              </div>
            ))
          ) : contacts.length === 0 ? (
            <Box className={classes.emptyStateBox} style={{ padding: "48px 16px" }}>
              <PeopleAltOutlinedIcon className={classes.emptyIcon} />
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Nenhum contato encontrado
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Tente alterar os filtros ou termos da pesquisa.
              </Typography>
            </Box>
          ) : (
            contacts.map((contact, idx) => {
              const cpf = formatCPF(getContactField(contact, "CPF"));
              const nasc = getContactField(contact, "Data de Nascimento");
              return (
                <div
                  key={contact.id}
                  className={`${classes.linha} ${classes.linhaCartao}`}
                  style={{ animationDelay: `${Math.min(idx, 12) * 0.025}s` }}
                  onClick={() => handleEditContact(contact.id)}
                  title="Abrir ficha do contato"
                >
                  {/* Contato */}
                  <div className={classes.contactNameBox}>
                    <Avatar src={contact.profilePicUrl} className={classes.avatar}>
                      {contact.name ? contact.name.charAt(0).toUpperCase() : "?"}
                    </Avatar>
                    <div className={classes.contactNameTexto}>
                      <Typography className={classes.contactName}>
                        {formatarNomeOuTelefone(contact.name, contact.number) || "Sem Nome"}
                      </Typography>
                      <div className={classes.linhaTelefone}>
                        <WhatsAppIcon style={{ fontSize: 13, color: "#14b8a6" }} />
                        <span>{formatarTelefone(contact.number)}</span>
                        {renderConexao(contact)}
                        {chipInterno(contact)}
                      </div>
                    </div>
                  </div>

                  {/* Vaga */}
                  <div className={classes.colDoc}>{renderVaga(contact)}</div>

                  {/* Entrevista */}
                  <div className={classes.celulaChips}>{renderEntrevista(contact)}</div>

                  {/* Etiquetas */}
                  <div className={classes.celulaChips}>{renderEtiquetas(contact)}</div>

                  {/* Situacao */}
                  <div className={classes.colConsulta}>{renderSituacao(contact)}</div>

                  {/* Acoes: sempre visiveis */}
                  <div className={classes.actionButtons} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Iniciar atendimento" arrow>
                      <IconButton size="small" className={classes.actionBtnWhatsapp} onClick={() => handleSaveTicket(contact.id)}>
                        <WhatsAppIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Abrir ficha" arrow>
                      <IconButton size="small" className={classes.actionBtn} onClick={() => handleEditContact(contact.id)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Can
                      role={user.profile}
                      perform="contacts-page:deleteContact"
                      yes={() => (
                        <Tooltip title="Excluir contato" arrow>
                          <IconButton
                            size="small"
                            className={classes.actionBtnDelete}
                            onClick={() => {
                              setDeletingContact(contact);
                              setConfirmOpen(true);
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {}
        {!loading && totalCount > 0 && (
          <Box className={classes.paginationFooter}>
            <Typography className={classes.paginationInfo}>
              Mostrando {startIndex}-{endIndex} de {totalCount} contatos
            </Typography>
            <Pagination
              count={totalPages}
              page={pageNumber}
              onChange={(e, page) => setPageNumber(page)}
              color="primary"
              shape="rounded"
              size="medium"
              className={classes.paginationControls}
            />
          </Box>
        )}
      </Paper>
      </div>
    </MainContainer>
  );
};

export default Contacts;
